import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { postOnboarding } from "@/lib/api";

// Página de onboarding reutilizable (patrocinadores / marcas)
export default function OnboardingPage({ config }) {
  const { kind, accent, eyebrow, title, subtitle, questions } = config;
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const backTo = kind === "patrocinador" ? "/patrocinadores" : "/marcas";
  const inputCls =
    "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-ink outline-none transition-all focus:border-transparent focus:ring-2";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const answers = questions
        .filter((q) => values[q.name])
        .map((q) => ({ label: q.label, value: values[q.name] }));
      await postOnboarding({
        kind,
        name: values.__name || "",
        email: values.__email || "",
        company: values.__company || "",
        answers,
      });
      setDone(true);
      toast.success("¡Gracias! Recibimos tus respuestas.", {
        description: "Te contactamos en menos de 48 horas.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message || "No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Section className="min-h-[60vh]">
        <Reveal className="mx-auto max-w-lg rounded-[2.5rem] bg-white p-10 text-center shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
          <CheckCircle2 className="mx-auto h-14 w-14" style={{ color: accent }} />
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-ink">
            ¡Cuestionario recibido!
          </h2>
          <p className="mt-3 text-ink/70">
            Gracias por tus respuestas. Nuestro equipo te enviará una propuesta a la medida
            en menos de 48 horas.
          </p>
          <Link
            to={backTo}
            data-testid="onboarding-back"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-semibold text-cream transition-all hover:-translate-y-0.5"
          >
            <ArrowLeft className="h-4 w-4" /> Volver
          </Link>
        </Reveal>
      </Section>
    );
  }

  return (
    <>
      <PageHeader eyebrow={eyebrow} eyebrowColor={accent} title={title} subtitle={subtitle} />
      <Section className="!pt-4">
        <Link
          to={backTo}
          className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ink/50 transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>
        <Reveal>
          <form
            onSubmit={submit}
            className="mx-auto max-w-2xl space-y-5 rounded-[2rem] bg-white p-7 sm:p-10 shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
            data-testid="onboarding-form"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  Nombre de contacto
                </label>
                <input
                  data-testid="onboarding-name"
                  required
                  value={values.__name || ""}
                  onChange={(e) => set("__name", e.target.value)}
                  placeholder="Tu nombre"
                  className={inputCls}
                  style={{ "--tw-ring-color": accent }}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  Email
                </label>
                <input
                  data-testid="onboarding-email"
                  required
                  type="email"
                  value={values.__email || ""}
                  onChange={(e) => set("__email", e.target.value)}
                  placeholder="nombre@empresa.com"
                  className={inputCls}
                  style={{ "--tw-ring-color": accent }}
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                {kind === "patrocinador" ? "Empresa" : "Marca"}
              </label>
              <input
                data-testid="onboarding-company"
                required
                value={values.__company || ""}
                onChange={(e) => set("__company", e.target.value)}
                placeholder={kind === "patrocinador" ? "Nombre de la empresa" : "Nombre de la marca"}
                className={inputCls}
                style={{ "--tw-ring-color": accent }}
              />
            </div>

            <div className="border-t border-ink/10 pt-5" />

            {questions.map((q) => (
              <div key={q.name}>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-ink/60">
                  {q.label}
                </label>
                {q.type === "select" ? (
                  <select
                    data-testid={`onboarding-${q.name}`}
                    value={values[q.name] || ""}
                    onChange={(e) => set(q.name, e.target.value)}
                    className={inputCls}
                    style={{ "--tw-ring-color": accent }}
                  >
                    <option value="">Selecciona una opción</option>
                    {q.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : q.type === "textarea" ? (
                  <textarea
                    data-testid={`onboarding-${q.name}`}
                    rows={3}
                    value={values[q.name] || ""}
                    onChange={(e) => set(q.name, e.target.value)}
                    placeholder={q.placeholder}
                    className={`${inputCls} resize-none`}
                    style={{ "--tw-ring-color": accent }}
                  />
                ) : (
                  <input
                    data-testid={`onboarding-${q.name}`}
                    value={values[q.name] || ""}
                    onChange={(e) => set(q.name, e.target.value)}
                    placeholder={q.placeholder}
                    className={inputCls}
                    style={{ "--tw-ring-color": accent }}
                  />
                )}
              </div>
            ))}

            <button
              type="submit"
              data-testid="onboarding-submit"
              disabled={loading}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70"
              style={{ backgroundColor: accent }}
            >
              {loading ? "Enviando..." : "Enviar cuestionario"}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </form>
        </Reveal>
      </Section>
    </>
  );
}
